import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixMembersAboutV2MemberAbout from './members-about-v2-member-about-members-about.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/** The member about section contains content fields that a member can add information to on their profile. */
export interface MemberAbout {
  /**
   * ID of the "About" section content.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** Revision number, which increments by 1 each time the "About" section content is updated. To prevent conflicting changes, the existing revision must be used when updating an "About" section content. */
  revision?: string | null;
  /**
   * Member ID.
   * @format GUID
   */
  memberId?: string | null;
  /** <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.video.divider.giphy.emoji.link" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>. */
  content?: RichContent;
}

export interface RichContent {
  /** Node objects representing a rich content document. */
  nodes?: Node[];
  /** Object metadata. */
  metadata?: Metadata;
  /** Global styling for header, paragraph, block quote, and code block nodes in the object. */
  documentStyle?: DocumentStyle;
}

export interface Node extends NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Data for a card node. */
  cardData?: CardData;
  /** Data for a table of contents node. */
  tocData?: TocData;
  /** Node type. Use `APP_EMBED` for nodes that embed content from other Wix apps. Use `EMBED` to embed content in [oEmbed](https://oembed.com/) format. */
  type?: NodeTypeWithLiterals;
  /** Node ID. */
  id?: string;
  /** A list of child nodes. */
  nodes?: Node[];
  /** Padding and background color styling for the node. */
  style?: NodeStyle;
}

/** @oneof */
export interface NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Data for a card node. */
  cardData?: CardData;
  /** Data for a table of contents node. */
  tocData?: TocData;
}

export enum NodeType {
  PARAGRAPH = 'PARAGRAPH',
  TEXT = 'TEXT',
  HEADING = 'HEADING',
  BULLETED_LIST = 'BULLETED_LIST',
  ORDERED_LIST = 'ORDERED_LIST',
  LIST_ITEM = 'LIST_ITEM',
  BLOCKQUOTE = 'BLOCKQUOTE',
  CODE_BLOCK = 'CODE_BLOCK',
  VIDEO = 'VIDEO',
  DIVIDER = 'DIVIDER',
  FILE = 'FILE',
  GALLERY = 'GALLERY',
  GIF = 'GIF',
  HTML = 'HTML',
  IMAGE = 'IMAGE',
  LINK_PREVIEW = 'LINK_PREVIEW',
  /** @deprecated */
  MAP = 'MAP',
  POLL = 'POLL',
  APP_EMBED = 'APP_EMBED',
  BUTTON = 'BUTTON',
  COLLAPSIBLE_LIST = 'COLLAPSIBLE_LIST',
  TABLE = 'TABLE',
  EMBED = 'EMBED',
  COLLAPSIBLE_ITEM = 'COLLAPSIBLE_ITEM',
  COLLAPSIBLE_ITEM_TITLE = 'COLLAPSIBLE_ITEM_TITLE',
  COLLAPSIBLE_ITEM_BODY = 'COLLAPSIBLE_ITEM_BODY',
  TABLE_CELL = 'TABLE_CELL',
  TABLE_ROW = 'TABLE_ROW',
  EXTERNAL = 'EXTERNAL',
  AUDIO = 'AUDIO',
  CAPTION = 'CAPTION',
  LAYOUT = 'LAYOUT',
  LAYOUT_CELL = 'LAYOUT_CELL',
  SHAPE = 'SHAPE',
  CARD = 'CARD',
  TOC = 'TOC',
}

/** @enumType */
export type NodeTypeWithLiterals =
  | NodeType
  | 'PARAGRAPH'
  | 'TEXT'
  | 'HEADING'
  | 'BULLETED_LIST'
  | 'ORDERED_LIST'
  | 'LIST_ITEM'
  | 'BLOCKQUOTE'
  | 'CODE_BLOCK'
  | 'VIDEO'
  | 'DIVIDER'
  | 'FILE'
  | 'GALLERY'
  | 'GIF'
  | 'HTML'
  | 'IMAGE'
  | 'LINK_PREVIEW'
  | 'MAP'
  | 'POLL'
  | 'APP_EMBED'
  | 'BUTTON'
  | 'COLLAPSIBLE_LIST'
  | 'TABLE'
  | 'EMBED'
  | 'COLLAPSIBLE_ITEM'
  | 'COLLAPSIBLE_ITEM_TITLE'
  | 'COLLAPSIBLE_ITEM_BODY'
  | 'TABLE_CELL'
  | 'TABLE_ROW'
  | 'EXTERNAL'
  | 'AUDIO'
  | 'CAPTION'
  | 'LAYOUT'
  | 'LAYOUT_CELL'
  | 'SHAPE'
  | 'CARD'
  | 'TOC';

export interface NodeStyle {
  /** The top padding value in pixels. */
  paddingTop?: string | null;
  /** The bottom padding value in pixels. */
  paddingBottom?: string | null;
  /** The background color as a hexadecimal value. */
  backgroundColor?: string | null;
}

export interface ButtonData {
  /** Styling for the button's container. */
  containerData?: PluginContainerData;
  /** The button type. */
  type?: ButtonDataTypeWithLiterals;
  /** Styling for the button. */
  styles?: Styles;
  /** The text to display on the button. */
  text?: string | null;
  /** Button link details. */
  link?: Link;
}

/** Background type */
export enum BackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackgroundTypeWithLiterals = BackgroundType | 'COLOR' | 'GRADIENT';

export interface Gradient {
  /** Gradient type. */
  type?: GradientTypeWithLiterals;
  /**
   * Color stops for the gradient.
   * @maxSize 1000
   */
  stops?: Stop[];
  /** Angle in degrees for linear gradient (0-360). */
  angle?: number | null;
  /**
   * Horizontal center position for radial gradient (0-100).
   * @max 100
   */
  centerX?: number | null;
  /**
   * Vertical center position for radial gradient (0-100).
   * @max 100
   */
  centerY?: number | null;
}

/** Gradient type. */
export enum GradientType {
  /** Linear gradient. */
  LINEAR = 'LINEAR',
  /** Radial gradient. */
  RADIAL = 'RADIAL',
}

/** @enumType */
export type GradientTypeWithLiterals = GradientType | 'LINEAR' | 'RADIAL';

/** A single color stop in the gradient. */
export interface Stop {
  /**
   * Stop color as hex value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Stop position (0-1). */
  position?: number | null;
}

export interface Border {
  /**
   * Deprecated: Use `borderWidth` in `styles` instead.
   * @deprecated
   */
  width?: number | null;
  /**
   * Deprecated: Use `borderRadius` in `styles` instead.
   * @deprecated
   */
  radius?: number | null;
}

export interface Colors {
  /**
   * Deprecated: Use `textColor` in `styles` instead.
   * @deprecated
   */
  text?: string | null;
  /**
   * Deprecated: Use `borderColor` in `styles` instead.
   * @deprecated
   */
  border?: string | null;
  /**
   * Deprecated: Use `backgroundColor` in `styles` instead.
   * @deprecated
   */
  background?: string | null;
}

/** Background styling (color or gradient) */
export interface Background {
  /** Background type. */
  type?: BackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface PluginContainerData {
  /** The width of the node when it's displayed. */
  width?: PluginContainerDataWidth;
  /** The node's alignment within its container. */
  alignment?: PluginContainerDataAlignmentWithLiterals;
  /** Spoiler cover settings for the node. */
  spoiler?: Spoiler;
  /** The height of the node when it's displayed. */
  height?: Height;
  /** Sets whether text should wrap around this node when it's displayed. If `textWrap` is `false`, the node takes up the width of its container. Defaults to `true` for all node types except 'DIVIVDER' where it defaults to `false`. */
  textWrap?: boolean | null;
}

export enum WidthType {
  /** Width matches the content width */
  CONTENT = 'CONTENT',
  /** Small Width */
  SMALL = 'SMALL',
  /** Width will match the original asset width */
  ORIGINAL = 'ORIGINAL',
  /** coast-to-coast display */
  FULL_WIDTH = 'FULL_WIDTH',
}

/** @enumType */
export type WidthTypeWithLiterals =
  | WidthType
  | 'CONTENT'
  | 'SMALL'
  | 'ORIGINAL'
  | 'FULL_WIDTH';

export interface PluginContainerDataWidth
  extends PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

/** @oneof */
export interface PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

export enum PluginContainerDataAlignment {
  /** Center Alignment */
  CENTER = 'CENTER',
  /** Left Alignment */
  LEFT = 'LEFT',
  /** Right Alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type PluginContainerDataAlignmentWithLiterals =
  | PluginContainerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface Spoiler {
  /** Sets whether the spoiler cover is enabled for this node. Defaults to `false`. */
  enabled?: boolean | null;
  /** The description displayed on top of the spoiler cover. */
  description?: string | null;
  /** The text for the button used to remove the spoiler cover. */
  buttonText?: string | null;
}

export interface Height {
  /** A custom height value in pixels. */
  custom?: string | null;
}

export enum ButtonDataType {
  /** Regular link button */
  LINK = 'LINK',
  /** Triggers custom action that is defined in plugin configuration by the consumer */
  ACTION = 'ACTION',
}

/** @enumType */
export type ButtonDataTypeWithLiterals = ButtonDataType | 'LINK' | 'ACTION';

export interface Styles {
  /**
   * Deprecated: Use `borderWidth` and `borderRadius` instead.
   * @deprecated
   */
  border?: Border;
  /**
   * Deprecated: Use `textColor`, `borderColor` and `backgroundColor` instead.
   * @deprecated
   */
  colors?: Colors;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /**
   * Deprecated: Use `borderWidth` for normal/hover states instead.
   * @deprecated
   */
  borderWidthHover?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  textColorHover?: string | null;
  /**
   * Deprecated: Use `background` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backgroundColor?: string | null;
  /**
   * Deprecated: Use `backgroundHover` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
  /** Background styling (color or gradient). */
  background?: Background;
  /** Background styling for hover state (color or gradient). */
  backgroundHover?: Background;
}

export interface Link extends LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
  /**
   * he HTML `target` attribute value for the link. This property defines where the linked document opens as follows:
   * `SELF` - Default. Opens the linked document in the same frame as the link.
   * `BLANK` - Opens the linked document in a new browser tab or window.
   * `PARENT` - Opens the linked document in the link's parent frame.
   * `TOP` - Opens the linked document in the full body of the link's browser tab or window.
   */
  target?: TargetWithLiterals;
  /** The HTML `rel` attribute value for the link. This object specifies the relationship between the current document and the linked document. */
  rel?: Rel;
  /** A serialized object used for a custom or external link panel. */
  customData?: string | null;
}

/** @oneof */
export interface LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
}

export enum Target {
  /** Opens the linked document in the same frame as it was clicked (this is default) */
  SELF = 'SELF',
  /** Opens the linked document in a new window or tab */
  BLANK = 'BLANK',
  /** Opens the linked document in the parent frame */
  PARENT = 'PARENT',
  /** Opens the linked document in the full body of the window */
  TOP = 'TOP',
}

/** @enumType */
export type TargetWithLiterals = Target | 'SELF' | 'BLANK' | 'PARENT' | 'TOP';

export interface Rel {
  /** Indicates to search engine crawlers not to follow the link. Defaults to `false`. */
  nofollow?: boolean | null;
  /** Indicates to search engine crawlers that the link is a paid placement such as sponsored content or an advertisement. Defaults to `false`. */
  sponsored?: boolean | null;
  /** Indicates that this link is user-generated content and isn't necessarily trusted or endorsed by the page’s author. For example, a link in a fourm post. Defaults to `false`. */
  ugc?: boolean | null;
  /** Indicates that this link protect referral information from being passed to the target website. */
  noreferrer?: boolean | null;
}

export interface CodeBlockData {
  /** Styling for the code block's text. */
  textStyle?: TextStyle;
}

export interface TextStyle {
  /** Text alignment. Defaults to `AUTO`. */
  textAlignment?: TextAlignmentWithLiterals;
  /** A CSS `line-height` value for the text expressed as a ratio relative to the font size. For example, if the font size is 20px, a `lineHeight` value of `'1.5'`` results in a line height of 30px. */
  lineHeight?: string | null;
}

export enum TextAlignment {
  /** browser default, eqivalent to `initial` */
  AUTO = 'AUTO',
  /** Left align */
  LEFT = 'LEFT',
  /** Right align */
  RIGHT = 'RIGHT',
  /** Center align */
  CENTER = 'CENTER',
  /** Text is spaced to line up its left and right edges to the left and right edges of the line box, except for the last line */
  JUSTIFY = 'JUSTIFY',
}

/** @enumType */
export type TextAlignmentWithLiterals =
  | TextAlignment
  | 'AUTO'
  | 'LEFT'
  | 'RIGHT'
  | 'CENTER'
  | 'JUSTIFY';

export interface DividerData {
  /** Styling for the divider's container. */
  containerData?: PluginContainerData;
  /** Divider line style. */
  lineStyle?: LineStyleWithLiterals;
  /** Divider width. */
  width?: WidthWithLiterals;
  /** Divider alignment. */
  alignment?: DividerDataAlignmentWithLiterals;
}

export enum LineStyle {
  /** Single Line */
  SINGLE = 'SINGLE',
  /** Double Line */
  DOUBLE = 'DOUBLE',
  /** Dashed Line */
  DASHED = 'DASHED',
  /** Dotted Line */
  DOTTED = 'DOTTED',
}

/** @enumType */
export type LineStyleWithLiterals =
  | LineStyle
  | 'SINGLE'
  | 'DOUBLE'
  | 'DASHED'
  | 'DOTTED';

export enum Width {
  /** Large line */
  LARGE = 'LARGE',
  /** Medium line */
  MEDIUM = 'MEDIUM',
  /** Small line */
  SMALL = 'SMALL',
}

/** @enumType */
export type WidthWithLiterals = Width | 'LARGE' | 'MEDIUM' | 'SMALL';

export enum DividerDataAlignment {
  /** Center alignment */
  CENTER = 'CENTER',
  /** Left alignment */
  LEFT = 'LEFT',
  /** Right alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type DividerDataAlignmentWithLiterals =
  | DividerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface FileData {
  /** Styling for the file's container. */
  containerData?: PluginContainerData;
  /** The source for the file's data. */
  src?: FileSource;
  /** File name. */
  name?: string | null;
  /** File type. */
  type?: string | null;
  /**
   * Use `sizeInKb` instead.
   * @deprecated
   */
  size?: number | null;
  /** Settings for PDF files. */
  pdfSettings?: PDFSettings;
  /** File MIME type. */
  mimeType?: string | null;
  /** File path. */
  path?: string | null;
  /** File size in KB. */
  sizeInKb?: string | null;
}

export enum ViewMode {
  /** No PDF view */
  NONE = 'NONE',
  /** Full PDF view */
  FULL = 'FULL',
  /** Mini PDF view */
  MINI = 'MINI',
}

/** @enumType */
export type ViewModeWithLiterals = ViewMode | 'NONE' | 'FULL' | 'MINI';

export interface FileSource extends FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
  /** Indicates whether the file's source is private. Defaults to `false`. */
  private?: boolean | null;
}

/** @oneof */
export interface FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
}

export interface PDFSettings {
  /**
   * PDF view mode. One of the following:
   * `NONE` : The PDF isn't displayed.
   * `FULL` : A full page view of the PDF is displayed.
   * `MINI` : A mini view of the PDF is displayed.
   */
  viewMode?: ViewModeWithLiterals;
  /** Sets whether the PDF download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the PDF print button is disabled. Defaults to `false`. */
  disablePrint?: boolean | null;
}

export interface GalleryData {
  /** Styling for the gallery's container. */
  containerData?: PluginContainerData;
  /** The items in the gallery. */
  items?: Item[];
  /** Options for defining the gallery's appearance. */
  options?: GalleryOptions;
  /** Sets whether the gallery's expand button is disabled. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Sets whether the gallery's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
}

export interface Media {
  /** The source for the media's data. */
  src?: FileSource;
  /** Media width in pixels. */
  width?: number | null;
  /** Media height in pixels. */
  height?: number | null;
  /** Media duration in seconds. Only relevant for audio and video files. */
  duration?: number | null;
}

export interface Image {
  /** Image file details. */
  media?: Media;
  /** Link details for images that are links. */
  link?: Link;
}

export interface Video {
  /** Video file details. */
  media?: Media;
  /** Video thumbnail file details. */
  thumbnail?: Media;
}

export interface Item extends ItemDataOneOf {
  /** An image item. */
  image?: Image;
  /** A video item. */
  video?: Video;
  /** Item title. */
  title?: string | null;
  /** Item's alternative text. */
  altText?: string | null;
}

/** @oneof */
export interface ItemDataOneOf {
  /** An image item. */
  image?: Image;
  /** A video item. */
  video?: Video;
}

export interface GalleryOptions {
  /** Gallery layout. */
  layout?: GalleryOptionsLayout;
  /** Styling for gallery items. */
  item?: ItemStyle;
  /** Styling for gallery thumbnail images. */
  thumbnails?: Thumbnails;
}

export enum LayoutType {
  /** Collage type */
  COLLAGE = 'COLLAGE',
  /** Masonry type */
  MASONRY = 'MASONRY',
  /** Grid type */
  GRID = 'GRID',
  /** Thumbnail type */
  THUMBNAIL = 'THUMBNAIL',
  /** Slider type */
  SLIDER = 'SLIDER',
  /** Slideshow type */
  SLIDESHOW = 'SLIDESHOW',
  /** Panorama type */
  PANORAMA = 'PANORAMA',
  /** Column type */
  COLUMN = 'COLUMN',
  /** Magic type */
  MAGIC = 'MAGIC',
  /** Fullsize images type */
  FULLSIZE = 'FULLSIZE',
}

/** @enumType */
export type LayoutTypeWithLiterals =
  | LayoutType
  | 'COLLAGE'
  | 'MASONRY'
  | 'GRID'
  | 'THUMBNAIL'
  | 'SLIDER'
  | 'SLIDESHOW'
  | 'PANORAMA'
  | 'COLUMN'
  | 'MAGIC'
  | 'FULLSIZE';

export enum Orientation {
  /** Rows Orientation */
  ROWS = 'ROWS',
  /** Columns Orientation */
  COLUMNS = 'COLUMNS',
}

/** @enumType */
export type OrientationWithLiterals = Orientation | 'ROWS' | 'COLUMNS';

export enum Crop {
  /** Crop to fill */
  FILL = 'FILL',
  /** Crop to fit */
  FIT = 'FIT',
}

/** @enumType */
export type CropWithLiterals = Crop | 'FILL' | 'FIT';

export enum ThumbnailsAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Right alignment */
  RIGHT = 'RIGHT',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
  /** Left alignment */
  LEFT = 'LEFT',
  /** No thumbnail */
  NONE = 'NONE',
}

/** @enumType */
export type ThumbnailsAlignmentWithLiterals =
  | ThumbnailsAlignment
  | 'TOP'
  | 'RIGHT'
  | 'BOTTOM'
  | 'LEFT'
  | 'NONE';

export interface GalleryOptionsLayout {
  /** Gallery layout type. */
  type?: LayoutTypeWithLiterals;
  /** Sets whether horizontal scroll is enabled. Defaults to `true` unless the layout `type` is set to `GRID` or `COLLAGE`. */
  horizontalScroll?: boolean | null;
  /** Gallery orientation. */
  orientation?: OrientationWithLiterals;
  /** The number of columns to display on full size screens. */
  numberOfColumns?: number | null;
  /** The number of columns to display on mobile screens. */
  mobileNumberOfColumns?: number | null;
}

export interface ItemStyle {
  /** Desirable dimension for each item in pixels (behvaior changes according to gallery type) */
  targetSize?: number | null;
  /** Item ratio */
  ratio?: number | null;
  /** Sets how item images are cropped. */
  crop?: CropWithLiterals;
  /** The spacing between items in pixels. */
  spacing?: number | null;
}

export interface Thumbnails {
  /** Thumbnail alignment. */
  placement?: ThumbnailsAlignmentWithLiterals;
  /** Spacing between thumbnails in pixels. */
  spacing?: number | null;
}

export interface GIFData {
  /** Styling for the GIF's container. */
  containerData?: PluginContainerData;
  /** The source of the full size GIF. */
  original?: GIF;
  /** The source of the downsized GIF. */
  downsized?: GIF;
  /** Height in pixels. */
  height?: number;
  /** Width in pixels. */
  width?: number;
  /** Type of GIF (Sticker or NORMAL). Defaults to `NORMAL`. */
  gifType?: GIFTypeWithLiterals;
}

export interface GIF {
  /**
   * GIF format URL.
   * @format WEB_URL
   */
  gif?: string | null;
  /**
   * MP4 format URL.
   * @format WEB_URL
   */
  mp4?: string | null;
  /**
   * Thumbnail URL.
   * @format WEB_URL
   */
  still?: string | null;
}

export enum GIFType {
  NORMAL = 'NORMAL',
  STICKER = 'STICKER',
}

/** @enumType */
export type GIFTypeWithLiterals = GIFType | 'NORMAL' | 'STICKER';

export interface HeadingData {
  /** Heading level from 1-6. */
  level?: number;
  /** Styling for the heading text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
}

export interface HTMLData extends HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** The WixelWidget ID for AI_WIDGET source nodes. */
  widgetId?: string;
  /** Styling for the HTML node's container. Height property is irrelevant for HTML embeds when autoHeight is set to `true`. */
  containerData?: PluginContainerData;
  /** The type of HTML code. */
  source?: SourceWithLiterals;
  /** If container height is aligned with its content height. Defaults to `true`. */
  autoHeight?: boolean | null;
}

/** @oneof */
export interface HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** The WixelWidget ID for AI_WIDGET source nodes. */
  widgetId?: string;
}

export enum Source {
  HTML = 'HTML',
  ADSENSE = 'ADSENSE',
  AI = 'AI',
  AI_WIDGET = 'AI_WIDGET',
}

/** @enumType */
export type SourceWithLiterals =
  | Source
  | 'HTML'
  | 'ADSENSE'
  | 'AI'
  | 'AI_WIDGET';

export interface ImageData {
  /** Styling for the image's container. */
  containerData?: PluginContainerData;
  /** Image file details. */
  image?: Media;
  /** Link details for images that are links. */
  link?: Link;
  /** Sets whether the image expands to full screen when clicked. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Image's alternative text. */
  altText?: string | null;
  /**
   * Deprecated: use Caption node instead.
   * @deprecated
   */
  caption?: string | null;
  /** Sets whether the image's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the image is decorative and does not need an explanation. Defaults to `false`. */
  decorative?: boolean | null;
  /** Styling for the image. */
  styles?: ImageDataStyles;
}

export interface StylesBorder {
  /** Border width in pixels. */
  width?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Border radius in pixels. */
  radius?: number | null;
}

export interface ImageDataStyles {
  /** Border attributes. */
  border?: StylesBorder;
}

export interface LinkPreviewData {
  /** Styling for the link preview's container. */
  containerData?: PluginContainerData;
  /** Link details. */
  link?: Link;
  /** Preview title. */
  title?: string | null;
  /** Preview thumbnail URL. */
  thumbnailUrl?: string | null;
  /** Preview description. */
  description?: string | null;
  /** The preview content as HTML. */
  html?: string | null;
  /** Styling for the link preview. */
  styles?: LinkPreviewDataStyles;
}

export enum StylesPosition {
  /** Thumbnail positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Thumbnail positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Thumbnail positioned at the top */
  TOP = 'TOP',
  /** Thumbnail hidden and not displayed */
  HIDDEN = 'HIDDEN',
}

/** @enumType */
export type StylesPositionWithLiterals =
  | StylesPosition
  | 'START'
  | 'END'
  | 'TOP'
  | 'HIDDEN';

export interface LinkPreviewDataStyles {
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Title color as a hexadecimal value.
   * @format COLOR_HEX
   */
  titleColor?: string | null;
  /**
   * Subtitle color as a hexadecimal value.
   * @format COLOR_HEX
   */
  subtitleColor?: string | null;
  /**
   * Link color as a hexadecimal value.
   * @format COLOR_HEX
   */
  linkColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Position of thumbnail. Defaults to `START`. */
  thumbnailPosition?: StylesPositionWithLiterals;
}

export interface MapData {
  /** Styling for the map's container. */
  containerData?: PluginContainerData;
  /** Map settings. */
  mapSettings?: MapSettings;
}

export interface MapSettings {
  /** The address to display on the map. */
  address?: string | null;
  /** Sets whether the map is draggable. */
  draggable?: boolean | null;
  /** Sets whether the location marker is visible. */
  marker?: boolean | null;
  /** Sets whether street view control is enabled. */
  streetViewControl?: boolean | null;
  /** Sets whether zoom control is enabled. */
  zoomControl?: boolean | null;
  /** Location latitude. */
  lat?: number | null;
  /** Location longitude. */
  lng?: number | null;
  /** Location name. */
  locationName?: string | null;
  /** Sets whether view mode control is enabled. */
  viewModeControl?: boolean | null;
  /** Initial zoom value. */
  initialZoom?: number | null;
  /** Map type. `HYBRID` is a combination of the `ROADMAP` and `SATELLITE` map types. */
  mapType?: MapTypeWithLiterals;
}

export enum MapType {
  /** Roadmap map type */
  ROADMAP = 'ROADMAP',
  /** Satellite map type */
  SATELITE = 'SATELITE',
  /** Hybrid map type */
  HYBRID = 'HYBRID',
  /** Terrain map type */
  TERRAIN = 'TERRAIN',
}

/** @enumType */
export type MapTypeWithLiterals =
  | MapType
  | 'ROADMAP'
  | 'SATELITE'
  | 'HYBRID'
  | 'TERRAIN';

export interface ParagraphData {
  /** Styling for the paragraph text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
  /** Paragraph level */
  level?: number | null;
}

export interface PollData {
  /** Styling for the poll's container. */
  containerData?: PluginContainerData;
  /** Poll data. */
  poll?: Poll;
  /** Layout settings for the poll and voting options. */
  layout?: PollDataLayout;
  /** Styling for the poll and voting options. */
  design?: Design;
}

export enum ViewRole {
  /** Only Poll creator can view the results */
  CREATOR = 'CREATOR',
  /** Anyone who voted can see the results */
  VOTERS = 'VOTERS',
  /** Anyone can see the results, even if one didn't vote */
  EVERYONE = 'EVERYONE',
}

/** @enumType */
export type ViewRoleWithLiterals = ViewRole | 'CREATOR' | 'VOTERS' | 'EVERYONE';

export enum VoteRole {
  /** Logged in member */
  SITE_MEMBERS = 'SITE_MEMBERS',
  /** Anyone */
  ALL = 'ALL',
}

/** @enumType */
export type VoteRoleWithLiterals = VoteRole | 'SITE_MEMBERS' | 'ALL';

export interface Permissions {
  /** Sets who can view the poll results. */
  view?: ViewRoleWithLiterals;
  /** Sets who can vote. */
  vote?: VoteRoleWithLiterals;
  /** Sets whether one voter can vote multiple times. Defaults to `false`. */
  allowMultipleVotes?: boolean | null;
}

export interface Option {
  /** Option ID. */
  id?: string | null;
  /** Option title. */
  title?: string | null;
  /** The image displayed with the option. */
  image?: Media;
}

export interface Settings {
  /** Permissions settings for voting. */
  permissions?: Permissions;
  /** Sets whether voters are displayed in the vote results. Defaults to `true`. */
  showVoters?: boolean | null;
  /** Sets whether the vote count is displayed. Defaults to `true`. */
  showVotesCount?: boolean | null;
}

export enum PollLayoutType {
  /** List */
  LIST = 'LIST',
  /** Grid */
  GRID = 'GRID',
}

/** @enumType */
export type PollLayoutTypeWithLiterals = PollLayoutType | 'LIST' | 'GRID';

export enum PollLayoutDirection {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type PollLayoutDirectionWithLiterals =
  | PollLayoutDirection
  | 'LTR'
  | 'RTL';

export interface PollLayout {
  /** The layout for displaying the voting options. */
  type?: PollLayoutTypeWithLiterals;
  /** The direction of the text displayed in the voting options. Text can be displayed either right-to-left or left-to-right. */
  direction?: PollLayoutDirectionWithLiterals;
  /** Sets whether to display the main poll image. Defaults to `false`. */
  enableImage?: boolean | null;
}

export interface OptionLayout {
  /** Sets whether to display option images. Defaults to `false`. */
  enableImage?: boolean | null;
}

export enum PollDesignBackgroundType {
  /** Color background type */
  COLOR = 'COLOR',
  /** Image background type */
  IMAGE = 'IMAGE',
  /** Gradiant background type */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type PollDesignBackgroundTypeWithLiterals =
  | PollDesignBackgroundType
  | 'COLOR'
  | 'IMAGE'
  | 'GRADIENT';

export interface BackgroundGradient {
  /** The gradient angle in degrees. */
  angle?: number | null;
  /**
   * The start color as a hexademical value.
   * @format COLOR_HEX
   */
  startColor?: string | null;
  /**
   * The end color as a hexademical value.
   * @format COLOR_HEX
   */
  lastColor?: string | null;
}

export interface PollDesignBackground
  extends PollDesignBackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: Media;
  /** Details for a gradient background. */
  gradient?: BackgroundGradient;
  /** Background type. For each option, include the relevant details. */
  type?: PollDesignBackgroundTypeWithLiterals;
}

/** @oneof */
export interface PollDesignBackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: Media;
  /** Details for a gradient background. */
  gradient?: BackgroundGradient;
}

export interface PollDesign {
  /** Background styling. */
  background?: PollDesignBackground;
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface OptionDesign {
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface Poll {
  /** Poll ID. */
  id?: string | null;
  /** Poll title. */
  title?: string | null;
  /** Poll creator ID. */
  creatorId?: string | null;
  /** Main poll image. */
  image?: Media;
  /** Voting options. */
  options?: Option[];
  /** The poll's permissions and display settings. */
  settings?: Settings;
}

export interface PollDataLayout {
  /** Poll layout settings. */
  poll?: PollLayout;
  /** Voting otpions layout settings. */
  options?: OptionLayout;
}

export interface Design {
  /** Styling for the poll. */
  poll?: PollDesign;
  /** Styling for voting options. */
  options?: OptionDesign;
}

export interface TextData {
  /** The text to apply decorations to. */
  text?: string;
  /** The decorations to apply. */
  decorations?: Decoration[];
}

/** Adds appearence changes to text */
export interface Decoration extends DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
  /** The type of decoration to apply. */
  type?: DecorationTypeWithLiterals;
}

/** @oneof */
export interface DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
}

export enum DecorationType {
  BOLD = 'BOLD',
  ITALIC = 'ITALIC',
  UNDERLINE = 'UNDERLINE',
  SPOILER = 'SPOILER',
  ANCHOR = 'ANCHOR',
  MENTION = 'MENTION',
  LINK = 'LINK',
  COLOR = 'COLOR',
  FONT_SIZE = 'FONT_SIZE',
  EXTERNAL = 'EXTERNAL',
  STRIKETHROUGH = 'STRIKETHROUGH',
  SUPERSCRIPT = 'SUPERSCRIPT',
  SUBSCRIPT = 'SUBSCRIPT',
  FONT_FAMILY = 'FONT_FAMILY',
}

/** @enumType */
export type DecorationTypeWithLiterals =
  | DecorationType
  | 'BOLD'
  | 'ITALIC'
  | 'UNDERLINE'
  | 'SPOILER'
  | 'ANCHOR'
  | 'MENTION'
  | 'LINK'
  | 'COLOR'
  | 'FONT_SIZE'
  | 'EXTERNAL'
  | 'STRIKETHROUGH'
  | 'SUPERSCRIPT'
  | 'SUBSCRIPT'
  | 'FONT_FAMILY';

export interface AnchorData {
  /** The target node's ID. */
  anchor?: string;
}

export interface ColorData {
  /** The text's background color as a hexadecimal value. */
  background?: string | null;
  /** The text's foreground color as a hexadecimal value. */
  foreground?: string | null;
}

export interface LinkData {
  /** Link details. */
  link?: Link;
}

export interface MentionData {
  /** The mentioned user's name. */
  name?: string;
  /** The version of the user's name that appears after the `@` character in the mention. */
  slug?: string;
  /** Mentioned user's ID. */
  id?: string | null;
}

export interface FontSizeData {
  /** The units used for the font size. */
  unit?: FontTypeWithLiterals;
  /** Font size value. */
  value?: number | null;
}

export enum FontType {
  PX = 'PX',
  EM = 'EM',
}

/** @enumType */
export type FontTypeWithLiterals = FontType | 'PX' | 'EM';

export interface SpoilerData {
  /** Spoiler ID. */
  id?: string | null;
}

export interface FontFamilyData {
  /** @maxLength 1000 */
  value?: string | null;
}

export interface AppEmbedData extends AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
  /** The type of Wix App content being embedded. */
  type?: AppTypeWithLiterals;
  /** The ID of the embedded content. */
  itemId?: string | null;
  /** The name of the embedded content. */
  name?: string | null;
  /**
   * Deprecated: Use `image` instead.
   * @deprecated
   */
  imageSrc?: string | null;
  /** The URL for the embedded content. */
  url?: string | null;
  /** An image for the embedded content. */
  image?: Media;
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Whether to hide the title. */
  hideTitle?: boolean | null;
  /** Whether to hide the price. */
  hidePrice?: boolean | null;
  /** Whether to hide the description (Event and Booking). */
  hideDescription?: boolean | null;
  /** Whether to hide the date and time (Event). */
  hideDateTime?: boolean | null;
  /** Whether to hide the location (Event). */
  hideLocation?: boolean | null;
  /** Whether to hide the duration (Booking). */
  hideDuration?: boolean | null;
  /** Whether to hide the button. */
  hideButton?: boolean | null;
  /** Whether to hide the ribbon. */
  hideRibbon?: boolean | null;
  /** Button styling options. */
  buttonStyles?: ButtonStyles;
  /** Image styling options. */
  imageStyles?: ImageStyles;
  /** Ribbon styling options. */
  ribbonStyles?: RibbonStyles;
  /** Card styling options. */
  cardStyles?: CardStyles;
  /** Styling for the app embed's container. */
  containerData?: PluginContainerData;
  /** Pricing data for embedded Wix App content. */
  pricingData?: PricingData;
}

/** @oneof */
export interface AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
}

export enum Position {
  /** Image positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Image positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Image positioned at the top */
  TOP = 'TOP',
}

/** @enumType */
export type PositionWithLiterals = Position | 'START' | 'END' | 'TOP';

export enum AspectRatio {
  /** 1:1 aspect ratio */
  SQUARE = 'SQUARE',
  /** 16:9 aspect ratio */
  RECTANGLE = 'RECTANGLE',
}

/** @enumType */
export type AspectRatioWithLiterals = AspectRatio | 'SQUARE' | 'RECTANGLE';

export enum Resizing {
  /** Fill the container, may crop the image */
  FILL = 'FILL',
  /** Fit the image within the container */
  FIT = 'FIT',
}

/** @enumType */
export type ResizingWithLiterals = Resizing | 'FILL' | 'FIT';

export enum Placement {
  /** Ribbon placed on the image */
  IMAGE = 'IMAGE',
  /** Ribbon placed on the product information */
  PRODUCT_INFO = 'PRODUCT_INFO',
}

/** @enumType */
export type PlacementWithLiterals = Placement | 'IMAGE' | 'PRODUCT_INFO';

export enum Type {
  /** Card with visible border and background */
  CONTAINED = 'CONTAINED',
  /** Card without visible border */
  FRAMELESS = 'FRAMELESS',
}

/** @enumType */
export type TypeWithLiterals = Type | 'CONTAINED' | 'FRAMELESS';

export enum Alignment {
  /** Content aligned to start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Content centered */
  CENTER = 'CENTER',
  /** Content aligned to end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
}

/** @enumType */
export type AlignmentWithLiterals = Alignment | 'START' | 'CENTER' | 'END';

export enum Layout {
  /** Elements stacked vertically */
  STACKED = 'STACKED',
  /** Elements arranged horizontally */
  SIDE_BY_SIDE = 'SIDE_BY_SIDE',
}

/** @enumType */
export type LayoutWithLiterals = Layout | 'STACKED' | 'SIDE_BY_SIDE';

export enum AppType {
  PRODUCT = 'PRODUCT',
  EVENT = 'EVENT',
  BOOKING = 'BOOKING',
}

/** @enumType */
export type AppTypeWithLiterals = AppType | 'PRODUCT' | 'EVENT' | 'BOOKING';

export interface BookingData {
  /** Booking duration in minutes. */
  durations?: string | null;
}

export interface EventData {
  /** Event schedule. */
  scheduling?: string | null;
  /** Event location. */
  location?: string | null;
}

export interface ButtonStyles {
  /** Text to display on the button. */
  buttonText?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  textColorHover?: string | null;
  /**
   * Background color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
}

export interface ImageStyles {
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Position of image. Defaults to `START`. */
  imagePosition?: PositionWithLiterals;
  /** Aspect ratio for the image. Defaults to `SQUARE`. */
  aspectRatio?: AspectRatioWithLiterals;
  /** How the image should be resized. Defaults to `FILL`. */
  resizing?: ResizingWithLiterals;
  /**
   * Image border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Image border width in pixels. */
  borderWidth?: number | null;
  /** Image border radius in pixels. */
  borderRadius?: number | null;
}

export interface RibbonStyles {
  /** Text to display on the ribbon. */
  ribbonText?: string | null;
  /**
   * Ribbon background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Ribbon text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Ribbon border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Ribbon border width in pixels. */
  borderWidth?: number | null;
  /** Ribbon border radius in pixels. */
  borderRadius?: number | null;
  /** Placement of the ribbon. Defaults to `IMAGE`. */
  ribbonPlacement?: PlacementWithLiterals;
}

export interface CardStyles {
  /**
   * Card background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Card border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Card border width in pixels. */
  borderWidth?: number | null;
  /** Card border radius in pixels. */
  borderRadius?: number | null;
  /** Card type. Defaults to `CONTAINED`. */
  type?: TypeWithLiterals;
  /** Content alignment. Defaults to `START`. */
  alignment?: AlignmentWithLiterals;
  /** Layout for title and price. Defaults to `STACKED`. */
  titlePriceLayout?: LayoutWithLiterals;
  /**
   * Title text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  titleColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
}

export interface PricingData {
  /**
   * Minimum numeric price value as string (e.g., "10.99").
   * @decimalValue options { maxScale:2 }
   */
  valueFrom?: string | null;
  /**
   * Maximum numeric price value as string (e.g., "19.99").
   * @decimalValue options { maxScale:2 }
   */
  valueTo?: string | null;
  /**
   * Numeric price value as string after discount application (e.g., "15.99").
   * @decimalValue options { maxScale:2 }
   */
  discountedValue?: string | null;
  /**
   * Currency of the value in ISO 4217 format (e.g., "USD", "EUR").
   * @format CURRENCY
   */
  currency?: string | null;
  /**
   * Pricing plan ID.
   * @format GUID
   */
  pricingPlanId?: string | null;
}

export interface VideoData {
  /** Styling for the video's container. */
  containerData?: PluginContainerData;
  /** Video details. */
  video?: Media;
  /** Video thumbnail details. */
  thumbnail?: Media;
  /** Sets whether the video's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Video title. */
  title?: string | null;
  /** Video options. */
  options?: PlaybackOptions;
}

export interface PlaybackOptions {
  /** Sets whether the media will automatically start playing. */
  autoPlay?: boolean | null;
  /** Sets whether media's will be looped. */
  playInLoop?: boolean | null;
  /** Sets whether media's controls will be shown. */
  showControls?: boolean | null;
}

export interface EmbedData {
  /** Styling for the oEmbed node's container. */
  containerData?: PluginContainerData;
  /** An [oEmbed](https://www.oembed.com) object. */
  oembed?: Oembed;
  /** Origin asset source. */
  src?: string | null;
}

export interface Oembed {
  /** The resource type. */
  type?: string | null;
  /** The width of the resource specified in the `url` property in pixels. */
  width?: number | null;
  /** The height of the resource specified in the `url` property in pixels. */
  height?: number | null;
  /** Resource title. */
  title?: string | null;
  /** The source URL for the resource. */
  url?: string | null;
  /** HTML for embedding a video player. The HTML should have no padding or margins. */
  html?: string | null;
  /** The name of the author or owner of the resource. */
  authorName?: string | null;
  /** The URL for the author or owner of the resource. */
  authorUrl?: string | null;
  /** The name of the resource provider. */
  providerName?: string | null;
  /** The URL for the resource provider. */
  providerUrl?: string | null;
  /** The URL for a thumbnail image for the resource. If this property is defined, `thumbnailWidth` and `thumbnailHeight` must also be defined. */
  thumbnailUrl?: string | null;
  /** The width of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailHeight` must also be defined. */
  thumbnailWidth?: string | null;
  /** The height of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailWidth`must also be defined. */
  thumbnailHeight?: string | null;
  /** The URL for an embedded viedo. */
  videoUrl?: string | null;
  /** The oEmbed version number.  This value must be `1.0`. */
  version?: string | null;
}

export interface CollapsibleListData {
  /** Styling for the collapsible list's container. */
  containerData?: PluginContainerData;
  /** If `true`, only one item can be expanded at a time. Defaults to `false`. */
  expandOnlyOne?: boolean | null;
  /** Sets which items are expanded when the page loads. */
  initialExpandedItems?: InitialExpandedItemsWithLiterals;
  /** The direction of the text in the list. Either left-to-right or right-to-left. */
  direction?: DirectionWithLiterals;
  /** If `true`, The collapsible item will appear in search results as an FAQ. */
  isQapageData?: boolean | null;
}

export enum InitialExpandedItems {
  /** First item will be expended initally */
  FIRST = 'FIRST',
  /** All items will expended initally */
  ALL = 'ALL',
  /** All items collapsed initally */
  NONE = 'NONE',
}

/** @enumType */
export type InitialExpandedItemsWithLiterals =
  | InitialExpandedItems
  | 'FIRST'
  | 'ALL'
  | 'NONE';

export enum Direction {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type DirectionWithLiterals = Direction | 'LTR' | 'RTL';

export interface TableData {
  /** Styling for the table's container. */
  containerData?: PluginContainerData;
  /** The table's dimensions. */
  dimensions?: Dimensions;
  /**
   * Deprecated: Use `rowHeader` and `columnHeader` instead.
   * @deprecated
   */
  header?: boolean | null;
  /** Sets whether the table's first row is a header. Defaults to `false`. */
  rowHeader?: boolean | null;
  /** Sets whether the table's first column is a header. Defaults to `false`. */
  columnHeader?: boolean | null;
  /** The spacing between cells in pixels. Defaults to `0`. */
  cellSpacing?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left.
   * @maxSize 4
   */
  cellPadding?: number[];
}

export interface Dimensions {
  /** An array representing relative width of each column in relation to the other columns. */
  colsWidthRatio?: number[];
  /** An array representing the height of each row in pixels. */
  rowsHeight?: number[];
  /** An array representing the minimum width of each column in pixels. */
  colsMinWidth?: number[];
}

export interface TableCellData {
  /** Styling for the cell's background color and text alignment. */
  cellStyle?: CellStyle;
  /** The cell's border colors. */
  borderColors?: BorderColors;
  /** Defines how many columns the cell spans. Default: 1. */
  colspan?: number | null;
  /** Defines how many rows the cell spans. Default: 1. */
  rowspan?: number | null;
  /** The cell's border widths. */
  borderWidths?: BorderWidths;
}

export enum VerticalAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentWithLiterals =
  | VerticalAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export interface CellStyle {
  /** Vertical alignment for the cell's text. */
  verticalAlignment?: VerticalAlignmentWithLiterals;
  /**
   * Cell background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
}

export interface BorderColors {
  /**
   * Left border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  left?: string | null;
  /**
   * Right border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  right?: string | null;
  /**
   * Top border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  top?: string | null;
  /**
   * Bottom border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  bottom?: string | null;
}

export interface BorderWidths {
  /** Left border width in pixels. */
  left?: number | null;
  /** Right border width in pixels. */
  right?: number | null;
  /** Top border width in pixels. */
  top?: number | null;
  /** Bottom border width in pixels. */
  bottom?: number | null;
}

/**
 * `NullValue` is a singleton enumeration to represent the null value for the
 * `Value` type union.
 *
 * The JSON representation for `NullValue` is JSON `null`.
 */
export enum NullValue {
  /** Null value. */
  NULL_VALUE = 'NULL_VALUE',
}

/** @enumType */
export type NullValueWithLiterals = NullValue | 'NULL_VALUE';

/**
 * `ListValue` is a wrapper around a repeated field of values.
 *
 * The JSON representation for `ListValue` is JSON array.
 */
export interface ListValue {
  /** Repeated field of dynamically typed values. */
  values?: any[];
}

export interface AudioData {
  /** Styling for the audio node's container. */
  containerData?: PluginContainerData;
  /** Audio file details. */
  audio?: Media;
  /** Sets whether the audio node's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Cover image. */
  coverImage?: Media;
  /** Track name. */
  name?: string | null;
  /** Author name. */
  authorName?: string | null;
  /** An HTML version of the audio node. */
  html?: string | null;
}

export interface OrderedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
  /** List start number. */
  start?: number | null;
}

export interface BulletedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
}

export interface BlockquoteData {
  /** Indentation level from 1-4. */
  indentation?: number;
}

export interface CaptionData {
  textStyle?: TextStyle;
}

export interface LayoutData {
  /**
   * Deprecated: Use `background` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backgroundColor?: string | null;
  /** Background image. */
  backgroundImage?: LayoutDataBackgroundImage;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Deprecated: Use `backdrop` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backdropColor?: string | null;
  /** Backdrop image. */
  backdropImage?: LayoutDataBackgroundImage;
  /** Backdrop top padding. */
  backdropPaddingTop?: number | null;
  /** Backdrop bottom padding */
  backdropPaddingBottom?: number | null;
  /** Horizontal and vertical gap between columns */
  gap?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left
   * @maxSize 4
   */
  cellPadding?: number[];
  /** Vertical alignment for the cell's items. */
  cellVerticalAlignment?: VerticalAlignmentAlignmentWithLiterals;
  /** Responsiveness behaviour of columns when responsiveness applies. Either stacks or wrappers. */
  responsivenessBehaviour?: ResponsivenessBehaviourWithLiterals;
  /** Size in pixels when responsiveness_behaviour applies */
  responsivenessBreakpoint?: number | null;
  /** Styling for the layout's container. */
  containerData?: PluginContainerData;
  /** Defines where selected design propertied applies to */
  designTarget?: DesignTargetWithLiterals;
  /** Banner configuration. When present, this layout is displayed as a banner. */
  banner?: Banner;
  /** Background styling (color or gradient). */
  background?: LayoutDataBackground;
  /** Backdrop styling (color or gradient). */
  backdrop?: Backdrop;
}

export enum ImageScalingScaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ImageScalingScalingWithLiterals =
  | ImageScalingScaling
  | 'AUTO'
  | 'CONTAIN'
  | 'COVER';

export enum ImagePosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionWithLiterals =
  | ImagePosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

export enum Origin {
  /** Banner originated from an image */
  IMAGE = 'IMAGE',
  /** Banner originated from a layout */
  LAYOUT = 'LAYOUT',
}

/** @enumType */
export type OriginWithLiterals = Origin | 'IMAGE' | 'LAYOUT';

/** Background type */
export enum LayoutDataBackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type LayoutDataBackgroundTypeWithLiterals =
  | LayoutDataBackgroundType
  | 'COLOR'
  | 'GRADIENT';

/** Backdrop type */
export enum BackdropType {
  /** Solid color backdrop */
  COLOR = 'COLOR',
  /** Gradient backdrop */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackdropTypeWithLiterals = BackdropType | 'COLOR' | 'GRADIENT';

export interface LayoutDataBackgroundImage {
  /** Background image. */
  media?: Media;
  /** Background image opacity. */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ImageScalingScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionWithLiterals;
}

export enum VerticalAlignmentAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentAlignmentWithLiterals =
  | VerticalAlignmentAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export enum ResponsivenessBehaviour {
  /** Stacking of columns */
  STACK = 'STACK',
  /** Wrapping of columns */
  WRAP = 'WRAP',
}

/** @enumType */
export type ResponsivenessBehaviourWithLiterals =
  | ResponsivenessBehaviour
  | 'STACK'
  | 'WRAP';

export enum DesignTarget {
  /** Design applied to layout */
  LAYOUT = 'LAYOUT',
  /** Design applied to cells */
  CELL = 'CELL',
}

/** @enumType */
export type DesignTargetWithLiterals = DesignTarget | 'LAYOUT' | 'CELL';

export interface Banner {
  /** Origin of the banner */
  origin?: OriginWithLiterals;
}

/** Background styling (color or gradient) */
export interface LayoutDataBackground {
  /** Background type. */
  type?: LayoutDataBackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

/** Backdrop styling (color or gradient) */
export interface Backdrop {
  /** Backdrop type. */
  type?: BackdropTypeWithLiterals;
  /**
   * Backdrop color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface LayoutCellData {
  /** Size of the cell in 12 columns grid. */
  colSpan?: number | null;
}

export interface ShapeData {
  /** Styling for the shape's container. */
  containerData?: PluginContainerData;
  /** Shape file details. */
  shape?: Media;
  /** Styling for the shape. */
  styles?: ShapeDataStyles;
}

export interface ShapeDataStyles {
  /**
   * Shape fill color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Map of original color keys to their new color values. */
  colors?: Record<string, string>;
}

export interface CardData {
  /** Background styling (color or gradient). */
  background?: CardDataBackground;
  /** Background image. */
  backgroundImage?: BackgroundImage;
}

export enum Scaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ScalingWithLiterals = Scaling | 'AUTO' | 'CONTAIN' | 'COVER';

export enum ImagePositionPosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionPositionWithLiterals =
  | ImagePositionPosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

/** Background type */
export enum CardDataBackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type CardDataBackgroundTypeWithLiterals =
  | CardDataBackgroundType
  | 'COLOR'
  | 'GRADIENT';

/** Background styling (color or gradient) */
export interface CardDataBackground {
  /** Background type. */
  type?: CardDataBackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface BackgroundImage {
  /** Background image. */
  media?: Media;
  /** Background image opacity. */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionPositionWithLiterals;
}

export interface TocData {
  /** Heading levels included in the table of contents. Default: [1, 2, 3, 4, 5, 6]. */
  includedHeadings?: number[];
  /** List style. Default: PLAIN. */
  listStyle?: ListStyleWithLiterals;
  /** Optional override for the font size in pixels. */
  fontSize?: number | null;
  /** Optional override for the vertical spacing between items in pixels. */
  itemSpacing?: number | null;
  /**
   * Optional override for the text color.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Indentation style. Default: NESTED. */
  indentation?: IndentationWithLiterals;
}

/** List style. */
export enum ListStyle {
  /** No markers (default) */
  PLAIN = 'PLAIN',
  /** Numbered list */
  NUMBERED = 'NUMBERED',
  /** Alphabetic letters */
  LETTERS = 'LETTERS',
  /** Roman numerals */
  ROMAN = 'ROMAN',
  /** Bulleted list */
  BULLETED = 'BULLETED',
  /** Alphabetical index */
  ALPHABETICAL_INDEX = 'ALPHABETICAL_INDEX',
  /** Alphabetical index (compact top-row only) */
  ALPHABETICAL_INDEX_COMPACT = 'ALPHABETICAL_INDEX_COMPACT',
}

/** @enumType */
export type ListStyleWithLiterals =
  | ListStyle
  | 'PLAIN'
  | 'NUMBERED'
  | 'LETTERS'
  | 'ROMAN'
  | 'BULLETED'
  | 'ALPHABETICAL_INDEX'
  | 'ALPHABETICAL_INDEX_COMPACT';

/** Indentation style. */
export enum Indentation {
  /** Sub-headings indented under parents (default) */
  NESTED = 'NESTED',
  /** All items at the same level */
  FLAT = 'FLAT',
}

/** @enumType */
export type IndentationWithLiterals = Indentation | 'NESTED' | 'FLAT';

export interface Metadata {
  /** Schema version. */
  version?: number;
  /**
   * When the object was created.
   * @readonly
   * @deprecated
   */
  createdTimestamp?: Date | null;
  /**
   * When the object was most recently updated.
   * @deprecated
   */
  updatedTimestamp?: Date | null;
  /** Object ID. */
  _id?: string | null;
}

export interface DocumentStyle {
  /** Styling for H1 nodes. */
  headerOne?: TextNodeStyle;
  /** Styling for H2 nodes. */
  headerTwo?: TextNodeStyle;
  /** Styling for H3 nodes. */
  headerThree?: TextNodeStyle;
  /** Styling for H4 nodes. */
  headerFour?: TextNodeStyle;
  /** Styling for H5 nodes. */
  headerFive?: TextNodeStyle;
  /** Styling for H6 nodes. */
  headerSix?: TextNodeStyle;
  /** Styling for paragraph nodes. */
  paragraph?: TextNodeStyle;
  /** Styling for block quote nodes. */
  blockquote?: TextNodeStyle;
  /** Styling for code block nodes. */
  codeBlock?: TextNodeStyle;
}

export interface TextNodeStyle {
  /** The decorations to apply to the node. */
  decorations?: Decoration[];
  /** Padding and background color for the node. */
  nodeStyle?: NodeStyle;
  /** Line height for text in the node. */
  lineHeight?: string | null;
}

export interface CreateMemberAboutRequest {
  /** The "About" section content to create. */
  memberAbout: MemberAbout;
}

export interface CreateMemberAboutResponse {
  /** The created "About" section content. */
  memberAbout?: MemberAbout;
}

export interface UpdateMemberAboutRequest {
  /** The "About" section content to update. */
  memberAbout: MemberAbout;
}

export interface UpdateMemberAboutResponse {
  /** The updated "About" section content. */
  memberAbout?: MemberAbout;
}

export interface DeleteMemberAboutRequest {
  /**
   * ID of the "About" section content to delete.
   * @format GUID
   */
  _id: string;
}

export interface DeleteMemberAboutResponse {}

export interface GetMemberAboutRequest {
  /**
   * ID of the "About" section content to retrieve.
   * @format GUID
   */
  _id: string;
}

export interface GetMemberAboutResponse {
  /** The retrieved "About" section content. */
  memberAbout?: MemberAbout;
}

export interface GetMyMemberAboutRequest {}

export interface GetMyMemberAboutResponse {
  /** The "About" section content of the current member. */
  memberAbout?: MemberAbout;
}

export interface QueryMemberAboutsRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more details. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /** Name of the field to sort by. */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  /** Sort is in ascending order. */
  ASC = 'ASC',
  /** Sort is in the descending order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface QueryMemberAboutsResponse {
  /** The retrieved content from the "About" sections. */
  memberAbouts?: MemberAbout[];
  /** Metadata for the paginated results. */
  metadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface MemberAboutCreatedEnvelope {
  entity: MemberAbout;
  metadata: EventMetadata;
}

/**
 * Triggered when a member's "About" section content is created.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Members
 * @permissionScopeId SCOPE.DC-MEMBERS.MANAGE-MEMBERS
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Challenges
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionScope Manage Portfolio
 * @permissionScopeId SCOPE.PORTFOLIO.MANAGE-PORTFOLIO
 * @permissionScope View SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.VIEW-SEO
 * @permissionScope Manage SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.MANAGE-SEO
 * @permissionScope Access Verticals by Automations
 * @permissionScopeId SCOPE.CRM.ACCESS-VERTICALS-BY-AUTOMATIONS
 * @permissionScope Read Members and Contacts - all read permissions
 * @permissionScopeId SCOPE.DC-CONTACTS-MEGA.READ-MEMBERS-CONTACTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Read Members
 * @permissionScopeId SCOPE.DC-MEMBERS.READ-MEMBERS
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionScope Manage Members and Contacts - all permissions
 * @permissionScopeId SCOPE.DC-CONTACTS-MEGA.MANAGE-MEMBERS-CONTACTS
 * @permissionId MEMBERS.MEMBER_ABOUT_READ
 * @webhook
 * @eventType wix.members.about.v2.member_about_created
 * @serviceIdentifier wix.members.about.v2.MembersAbout
 * @slug created
 */
export declare function onMemberAboutCreated(
  handler: (event: MemberAboutCreatedEnvelope) => void | Promise<void>
): void;

export interface MemberAboutDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when a member's "About" section content is deleted.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Members
 * @permissionScopeId SCOPE.DC-MEMBERS.MANAGE-MEMBERS
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Challenges
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionScope Manage Portfolio
 * @permissionScopeId SCOPE.PORTFOLIO.MANAGE-PORTFOLIO
 * @permissionScope View SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.VIEW-SEO
 * @permissionScope Manage SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.MANAGE-SEO
 * @permissionScope Access Verticals by Automations
 * @permissionScopeId SCOPE.CRM.ACCESS-VERTICALS-BY-AUTOMATIONS
 * @permissionScope Read Members and Contacts - all read permissions
 * @permissionScopeId SCOPE.DC-CONTACTS-MEGA.READ-MEMBERS-CONTACTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Read Members
 * @permissionScopeId SCOPE.DC-MEMBERS.READ-MEMBERS
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionScope Manage Members and Contacts - all permissions
 * @permissionScopeId SCOPE.DC-CONTACTS-MEGA.MANAGE-MEMBERS-CONTACTS
 * @permissionId MEMBERS.MEMBER_ABOUT_READ
 * @webhook
 * @eventType wix.members.about.v2.member_about_deleted
 * @serviceIdentifier wix.members.about.v2.MembersAbout
 * @slug deleted
 */
export declare function onMemberAboutDeleted(
  handler: (event: MemberAboutDeletedEnvelope) => void | Promise<void>
): void;

export interface MemberAboutUpdatedEnvelope {
  entity: MemberAbout;
  metadata: EventMetadata;
}

/**
 * Triggered when a member's "About" section content is updated.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Members
 * @permissionScopeId SCOPE.DC-MEMBERS.MANAGE-MEMBERS
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Challenges
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionScope Manage Portfolio
 * @permissionScopeId SCOPE.PORTFOLIO.MANAGE-PORTFOLIO
 * @permissionScope View SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.VIEW-SEO
 * @permissionScope Manage SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.MANAGE-SEO
 * @permissionScope Access Verticals by Automations
 * @permissionScopeId SCOPE.CRM.ACCESS-VERTICALS-BY-AUTOMATIONS
 * @permissionScope Read Members and Contacts - all read permissions
 * @permissionScopeId SCOPE.DC-CONTACTS-MEGA.READ-MEMBERS-CONTACTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Read Members
 * @permissionScopeId SCOPE.DC-MEMBERS.READ-MEMBERS
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionScope Manage Members and Contacts - all permissions
 * @permissionScopeId SCOPE.DC-CONTACTS-MEGA.MANAGE-MEMBERS-CONTACTS
 * @permissionId MEMBERS.MEMBER_ABOUT_READ
 * @webhook
 * @eventType wix.members.about.v2.member_about_updated
 * @serviceIdentifier wix.members.about.v2.MembersAbout
 * @slug updated
 */
export declare function onMemberAboutUpdated(
  handler: (event: MemberAboutUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates the "About" section content for a member.
 * @param memberAbout - The "About" section content to create.
 * @public
 * @requiredField memberAbout
 * @requiredField memberAbout.content
 * @permissionId MEMBERS.MEMBER_ABOUT_CREATE
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @returns The created "About" section content.
 * @fqn wix.members.about.v2.MembersAbout.CreateMemberAbout
 */
export async function createMemberAbout(
  memberAbout: NonNullablePaths<MemberAbout, `content`, 2>
): Promise<MemberAbout> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { memberAbout: memberAbout },
    ['memberAbout.content']
  );

  const reqOpts =
    ambassadorWixMembersAboutV2MemberAbout.createMemberAbout(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [
      'memberAbout.content',
    ])?.memberAbout!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { memberAbout: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['memberAbout']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates the "About" section content for a member.
 *
 * Each time the "About" section content is updated, `revision` increments by 1. The existing `revision` must be included when updating the "About" section content. This ensures you're working with the latest "About" section and prevents unintended overwrites.
 * @param _id - ID of the "About" section content.
 * @public
 * @requiredField _id
 * @requiredField memberAbout
 * @requiredField memberAbout.content
 * @requiredField memberAbout.revision
 * @permissionId MEMBERS.MEMBER_ABOUT_UPDATE
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @returns The updated "About" section content.
 * @fqn wix.members.about.v2.MembersAbout.UpdateMemberAbout
 */
export async function updateMemberAbout(
  _id: string,
  memberAbout: NonNullablePaths<UpdateMemberAbout, `content` | `revision`, 2>
): Promise<MemberAbout> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { memberAbout: { ...memberAbout, id: _id } },
    ['memberAbout.content']
  );

  const reqOpts =
    ambassadorWixMembersAboutV2MemberAbout.updateMemberAbout(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [
      'memberAbout.content',
    ])?.memberAbout!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { memberAbout: '$[1]' },
        explicitPathsToArguments: { 'memberAbout.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'memberAbout']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateMemberAbout {
  /**
   * ID of the "About" section content.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** Revision number, which increments by 1 each time the "About" section content is updated. To prevent conflicting changes, the existing revision must be used when updating an "About" section content. */
  revision?: string | null;
  /**
   * Member ID.
   * @format GUID
   */
  memberId?: string | null;
  /** <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.video.divider.giphy.emoji.link" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget>. */
  content?: RichContent;
}

/**
 * Deletes the "About" section content for a member.
 * @param _id - ID of the "About" section content to delete.
 * @public
 * @requiredField _id
 * @permissionId MEMBERS.MEMBER_ABOUT_DELETE
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @fqn wix.members.about.v2.MembersAbout.DeleteMemberAbout
 */
export async function deleteMemberAbout(_id: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ id: _id }, []);

  const reqOpts =
    ambassadorWixMembersAboutV2MemberAbout.deleteMemberAbout(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a member's "About" section content.
 * @param _id - ID of the "About" section content to retrieve.
 * @public
 * @requiredField _id
 * @permissionId MEMBERS.MEMBER_ABOUT_READ
 * @applicableIdentity APP
 * @returns The retrieved "About" section content.
 * @fqn wix.members.about.v2.MembersAbout.GetMemberAbout
 */
export async function getMemberAbout(_id: string): Promise<MemberAbout> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ id: _id }, []);

  const reqOpts =
    ambassadorWixMembersAboutV2MemberAbout.getMemberAbout(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [
      'memberAbout.content',
    ])?.memberAbout!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves the "About" section content of the current member.
 *
 * >**Note:**
 * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
 * @public
 * @permissionId MEMBERS.MEMBER_ABOUT_READ
 * @applicableIdentity APP
 * @fqn wix.members.about.v2.MembersAbout.GetMyMemberAbout
 */
export async function getMyMemberAbout(): Promise<GetMyMemberAboutResponse> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({}, []);

  const reqOpts =
    ambassadorWixMembersAboutV2MemberAbout.getMyMemberAbout(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [
      'memberAbout.content',
    ])!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of "About" sections.
 *
 * The `queryMemberAbouts()` method builds a query to retrieve a list of "About" sections and returns a [`MemberAboutsQueryBuilder`](https://dev.wix.com/docs/sdk/backend-modules/members/members-about/member-abouts-query-builder/eq) object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/members/members-about/member-abouts-query-builder/find) method.
 *
 * You can refine the query by chaining `MemberAboutsQueryBuilder` methods onto the query. `MemberAboutsQueryBuilder` methods enable you to sort, filter, and control the results `queryMemberAbouts()` returns.
 *
 * `queryMemberAbouts()` runs with these `MemberAboutsQueryBuilder` defaults, which you can override:
 *
 * - [`limit(50)`](https://dev.wix.com/docs/sdk/backend-modules/members/members-about/member-abouts-query-builder/limit)
 * @public
 * @permissionId MEMBERS.MEMBER_ABOUT_READ
 * @applicableIdentity APP
 * @fqn wix.members.about.v2.MembersAbout.QueryMemberAbouts
 */
export function queryMemberAbouts(): MemberAboutsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    MemberAbout,
    'CURSOR',
    QueryMemberAboutsRequest,
    QueryMemberAboutsResponse
  >({
    func: async (payload: QueryMemberAboutsRequest) => {
      const reqOpts =
        ambassadorWixMembersAboutV2MemberAbout.queryMemberAbouts(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryMemberAboutsRequest['query']) => {
      const args = [query, {}] as [QueryMemberAboutsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest(
        { ...args?.[1], query: args?.[0] },
        []
      );
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryMemberAboutsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, []),
        ['memberAbouts.content']
      );

      return {
        items: transformedData?.memberAbouts,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface MemberAboutsQueryResult extends QueryCursorResult {
  items: MemberAbout[];
  query: MemberAboutsQueryBuilder;
  next: () => Promise<MemberAboutsQueryResult>;
  prev: () => Promise<MemberAboutsQueryResult>;
}

export interface MemberAboutsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName: '_id' | 'memberId',
    value: any
  ) => MemberAboutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName: '_id' | 'memberId',
    value: any
  ) => MemberAboutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: '_id' | 'memberId',
    value: any
  ) => MemberAboutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: '_id' | 'memberId',
    value: any
  ) => MemberAboutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: '_id' | 'memberId',
    value: any
  ) => MemberAboutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: '_id' | 'memberId',
    value: any
  ) => MemberAboutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'memberId',
    value: string
  ) => MemberAboutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName: '_id' | 'memberId',
    value: any[]
  ) => MemberAboutsQueryBuilder;
  in: (
    propertyName: '_id' | 'memberId',
    value: any
  ) => MemberAboutsQueryBuilder;
  exists: (
    propertyName: '_id' | 'memberId',
    value: boolean
  ) => MemberAboutsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => MemberAboutsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => MemberAboutsQueryBuilder;
  find: () => Promise<MemberAboutsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.members.about.v2.MembersAbout.QueryMemberAbouts
 * @requiredField query
 */
export async function typedQueryMemberAbouts(
  query: MemberAboutQuery
): Promise<NonNullablePaths<QueryMemberAboutsResponse, `memberAbouts`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query }, []);

  const reqOpts =
    ambassadorWixMembersAboutV2MemberAbout.queryMemberAbouts(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [
      'memberAbouts.content',
    ])!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface MemberAboutQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_id', 'memberId'];
      operators: '*';
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  MemberAbout,
  MemberAboutQuerySpec
>;
export type MemberAboutQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Number of items to load. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request.  
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`  
  */
  sort?: {
    /** 
  Name of the field to sort by.  
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<MemberAbout, MemberAboutQuerySpec, MemberAboutQuery>(),
  },
};
